import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Loader2, XCircle, Clock, PawPrint, Building2, CreditCard } from 'lucide-react';

const PaymentSuccess = ({ user, type = 'microchip' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const transferId = searchParams.get('transfer_id');
  const partnerId = searchParams.get('partner_id');
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const [paymentData, setPaymentData] = useState(null);

  // Determine payment type from route
  const getPaymentType = () => {
    if (location.pathname.includes('transfer-success')) return 'transfer';
    if (location.pathname.includes('partner-success')) return 'partner';
    if (location.pathname.includes('subscription-success')) return 'subscription';
    return 'microchip';
  };

  const paymentType = getPaymentType();

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else if (transferId) {
      checkTransferStatus();
    } else if (partnerId) {
      checkPartnerStatus();
    } else {
      // No ID provided, show success by default
      setStatus('completed');
    }
  }, [sessionId, transferId, partnerId]);

  const checkPaymentStatus = async () => {
    try {
      const response = await axios.get(`/payments/status/${sessionId}`);
      setPaymentData(response.data);
      
      if (response.data.status === 'completed' || response.data.payment_status === 'paid') {
        setStatus('completed');
      } else if (attempts < 10) {
        setTimeout(() => {
          setAttempts(prev => prev + 1);
          checkPaymentStatus();
        }, 2000);
      } else {
        setStatus('timeout');
      }
    } catch (error) {
      console.error('Payment check failed:', error);
      if (attempts < 3) {
        setTimeout(() => {
          setAttempts(prev => prev + 1);
          checkPaymentStatus();
        }, 2000);
      } else {
        setStatus('error');
      }
    }
  };

  const checkTransferStatus = async () => {
    try {
      const response = await axios.get(`/transfers/${transferId}`);
      setPaymentData(response.data);
      
      if (response.data.status === 'completed' || response.data.payment_status === 'paid') {
        setStatus('completed');
      } else if (attempts < 10) {
        setTimeout(() => {
          setAttempts(prev => prev + 1);
          checkTransferStatus();
        }, 2000);
      } else {
        // Assume success after timeout - webhook may have processed it
        setStatus('completed');
      }
    } catch (error) {
      console.error('Transfer check failed:', error);
      // Assume success if we can't verify - the webhook will handle actual confirmation
      setStatus('completed');
    }
  };

  const checkPartnerStatus = async () => {
    // For partner registration, we show success immediately
    // The actual verification happens via webhook
    setStatus('completed');
  };

  const getSuccessContent = () => {
    switch (paymentType) {
      case 'transfer':
        return {
          icon: <PawPrint className="w-16 h-16 mx-auto text-green-600" />,
          title: 'Transfer Complete!',
          message: 'Congratulations! The pet has been successfully registered under your name. You can now access all pet records from your dashboard.',
          buttonText: 'Go to My Pets',
          buttonAction: () => navigate('/dashboard')
        };
      case 'partner':
        return {
          icon: <Building2 className="w-16 h-16 mx-auto text-green-600" />,
          title: 'Partnership Activated!',
          message: 'Welcome to the International Pet Registry network! Your registry is now connected. Admin will review and approve your connection shortly. Check your email for API credentials.',
          buttonText: 'Back to Home',
          buttonAction: () => navigate('/')
        };
      case 'subscription':
        return {
          icon: <CreditCard className="w-16 h-16 mx-auto text-green-600" />,
          title: 'Subscription Activated!',
          message: 'Your premium subscription is now active. Enjoy all the advanced features of the International Pet Registry platform.',
          buttonText: 'Return to Dashboard',
          buttonAction: () => navigate('/dashboard')
        };
      default:
        return {
          icon: <CheckCircle className="w-16 h-16 mx-auto text-green-600" />,
          title: 'Payment Successful!',
          message: "Your pet's microchip has been registered successfully. You can now view and manage your pet from your dashboard.",
          buttonText: 'Return to Dashboard',
          buttonAction: () => navigate('/dashboard')
        };
    }
  };

  const successContent = getSuccessContent();

  return (
    <div className="min-h-screen bg-[#fdfbf7] flex items-center justify-center p-4">
      <Card data-testid="payment-status" className="max-w-md w-full">
        <CardHeader>
          <CardTitle className="text-center">
            {status === 'checking' ? 'Processing Payment...' : 
             status === 'completed' ? 'Success!' : 
             status === 'timeout' ? 'Processing...' : 'Payment Status'}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center">
          {status === 'checking' && (
            <div className="space-y-4">
              <Loader2 className="w-16 h-16 animate-spin mx-auto text-primary" />
              <p className="text-stone-600">Verifying your payment...</p>
              <p className="text-sm text-stone-400">This may take a moment. Please don't close this page.</p>
            </div>
          )}

          {status === 'completed' && (
            <div className="space-y-4">
              {successContent.icon}
              <h3 className="text-2xl font-bold text-green-600">{successContent.title}</h3>
              <p className="text-stone-600">{successContent.message}</p>
              <Button 
                data-testid="back-dashboard-btn" 
                onClick={successContent.buttonAction} 
                className="bg-primary hover:bg-primary/90"
              >
                {successContent.buttonText}
              </Button>
            </div>
          )}

          {status === 'timeout' && (
            <div className="space-y-4">
              <Clock className="w-16 h-16 mx-auto text-yellow-500" />
              <h3 className="text-xl font-bold text-yellow-600">Payment Processing</h3>
              <p className="text-stone-600">
                Your payment is being processed. You should receive a confirmation email shortly.
              </p>
              <Button 
                data-testid="back-dashboard-btn" 
                onClick={() => navigate('/dashboard')} 
                className="bg-primary hover:bg-primary/90"
              >
                Return to Dashboard
              </Button>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-4">
              <XCircle className="w-16 h-16 mx-auto text-red-500" />
              <h3 className="text-xl font-bold text-red-600">Verification Failed</h3>
              <p className="text-stone-600">
                We couldn't verify your payment. If you completed the payment, please check your email for confirmation or contact support.
              </p>
              <div className="flex gap-2 justify-center">
                <Button 
                  variant="outline"
                  onClick={() => window.location.reload()}
                >
                  Try Again
                </Button>
                <Button 
                  data-testid="back-dashboard-btn" 
                  onClick={() => navigate('/dashboard')} 
                  className="bg-primary hover:bg-primary/90"
                >
                  Return to Dashboard
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
